import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Image as ImageIcon, Upload, Trash2, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import api, { dokumenteAPI } from '@/lib/api';

/* Tab „Dokumente" am Mitglied (22.09.2026, Omoplata-Parität 1.a):
   Ausweis, Gesundheitsfragebogen, Urkunde, Vertrag (Scan), Einwilligung,
   Sonstiges — PDF oder Bild bis 15 MB, verschlüsselt abgelegt. */

const TYP_LABEL = { ausweis: 'Ausweis', gesundheit: 'Gesundheitsfragebogen', urkunde: 'Urkunde', vertrag: 'Vertrag (Scan)', einwilligung: 'Einwilligung', sonstiges: 'Sonstiges' };
const groesse = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const datum = (iso) => { try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return ''; } };

export default function DokumenteTab({ leadId }) {
  const [liste, setListe] = useState([]);
  const [laden, setLaden] = useState(true);
  const [typ, setTyp] = useState('sonstiges');
  const [notiz, setNotiz] = useState('');
  const [busy, setBusy] = useState(false);
  const dateiRef = useRef(null);

  const lade = useCallback(async () => {
    setLaden(true);
    try { setListe((await dokumenteAPI.liste(leadId)).data.dokumente || []); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Dokumente konnten nicht geladen werden'); }
    finally { setLaden(false); }
  }, [leadId]);
  useEffect(() => { lade(); }, [lade]);

  const hochladen = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      await dokumenteAPI.hochladen(leadId, f, typ, notiz);
      toast.success(`${TYP_LABEL[typ]} abgelegt`);
      setNotiz('');
      lade();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload fehlgeschlagen'); }
    finally { setBusy(false); if (dateiRef.current) dateiRef.current.value = ''; }
  };

  const oeffnen = async (d) => {
    try {
      const r = await api.get(d.url.replace(/^\/api/, ''), { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error('Dokument konnte nicht geöffnet werden'); }
  };

  const loeschen = async (d) => {
    if (!window.confirm(`„${d.name}" endgültig löschen?`)) return;
    try { await dokumenteAPI.loeschen(d.id); toast.success('Gelöscht'); lade(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };

  return (
    <div className="space-y-4" data-testid="dokumente-tab">
      <Card className="border-border/60">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Art</label>
            <select value={typ} onChange={(e) => setTyp(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="dok-typ">
              {Object.entries(TYP_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="space-y-1 flex-1">
            <label className="text-xs text-muted-foreground">Notiz (optional)</label>
            <Input value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="z. B. Vorderseite, gültig bis 2029" className="h-9" data-testid="dok-notiz" />
          </div>
          <input ref={dateiRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={hochladen} data-testid="dok-datei" />
          <Button size="sm" className="h-9 gap-1.5" disabled={busy} onClick={() => dateiRef.current?.click()} data-testid="dok-hochladen">
            <Upload className="w-4 h-4" /> {busy ? 'Lädt…' : 'Datei ablegen'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardContent className="p-0">
          {laden ? <p className="p-4 text-sm text-muted-foreground">Lädt…</p>
            : liste.length === 0 ? <p className="p-6 text-sm text-muted-foreground text-center">Noch keine Dokumente — PDF oder Foto oben ablegen.</p>
            : (
              <ul className="divide-y divide-border/40">
                {liste.map((d) => (
                  <li key={d.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`dok-${d.id}`}>
                    {d.mimetype?.startsWith('image/') ? <ImageIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button type="button" className="font-medium hover:underline truncate text-left" onClick={() => oeffnen(d)}>{d.name}</button>
                        <Badge variant="outline" className="text-[10px]">{TYP_LABEL[d.typ] || d.typ}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{datum(d.erstellt_am)} · {groesse(d.groesse)}{d.hochgeladen_von ? ` · ${d.hochgeladen_von}` : ''}{d.notiz ? ` · ${d.notiz}` : ''}</p>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => oeffnen(d)} title="Öffnen"><ExternalLink className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500" onClick={() => loeschen(d)} title="Löschen"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </li>
                ))}
              </ul>
            )}
        </CardContent>
      </Card>
    </div>
  );
}
